import { useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify } from "ra-core";
import { useCallback } from "react";

import type { CalendarSyncResult, CalendarSyncTarget } from "../mail/mailTypes";
import type { CrmDataProvider } from "../providers/types";

/** The message a sync result deserves, or null when there is nothing to say. */
export const calendarSyncNotice = (
  result: CalendarSyncResult,
): {
  message: string;
  type: "info" | "warning";
  messageArgs?: Record<string, unknown>;
} | null => {
  if (result.past) return null;
  if (result.errors.some((error) => error.code === "sender_not_connected")) {
    return { message: "crm.calendar.sender_not_connected", type: "warning" };
  }
  if (result.errors.length > 0) {
    return {
      message: "crm.calendar.some_failed",
      type: "warning",
      messageArgs: { smart_count: result.errors.length },
    };
  }
  return result.calendars + result.invitations + result.removed > 0
    ? { message: "crm.calendar.synced", type: "info" }
    : null;
};

/**
 * Brings the staff calendars up to date after an event or the staff of an
 * opportunity changed. Never blocks the save: a failure is only reported.
 */
export const useCalendarSync = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const queryClient = useQueryClient();

  return useCallback(
    async (target: CalendarSyncTarget) => {
      try {
        const result = await dataProvider.syncCalendar(target);
        queryClient.invalidateQueries({
          queryKey: ["event_calendar_entries"],
        });
        const notice = calendarSyncNotice(result);
        if (notice) {
          notify(notice.message, {
            type: notice.type,
            messageArgs: notice.messageArgs,
          });
        }
      } catch {
        notify("crm.calendar.sync_failed", { type: "warning" });
      }
    },
    [dataProvider, notify, queryClient],
  );
};
